import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Layout from "../components/common/layout/Index";
import useTranslationHook from "../components/hooks/useTranslationHook";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSessionDetail } from "../redux/action/sessionAction";
import { handleSessionDetailData } from "../redux/slice/sessionSlice";
import {
    getSessionStatus,
    getSessionDate,
    formatPrice,
    getLotStatus,
    toEasternArabicNumerals,
} from "../helpers";
import SocketService from "../Service/SocketService";
import Shimmer from "../components/common/shimmer/Shimmer";
import moment from "moment";
import RegisterInterestModal from "../components/partials/property-details/right-panel/modals/RegisterInterestModal";
import useCountdownText from "../components/hooks/useCountdownText";

function StartsInBadge({ startDate }) {
    const countdown = useCountdownText(startDate);
    if (!countdown) return null;
    return (
        <li>
            <span className="timer">STARTS IN {countdown.toUpperCase()}</span>
        </li>
    );
}

function CountdownText({ startDate }) {
    return useCountdownText(startDate);
}

export default function SessionDetail() {
    const { t } = useTranslationHook();
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const lang = useSelector((state) => state.translation.lang);
    const { data, loading } = useSelector(
        (state) => state.session.sessionDetail
    );
    const user = useSelector((state) => state.auth.user);
    const isConnected = useSelector((state) => state.socket.isConnected);
    const [syncDetailData, setSyncDetailData] = useState(null);
    const [showRegisterInterestModal, setShowRegisterInterestModal] =
        useState(false);
    const [isRegisteredInterest, setIsRegisteredInterest] = useState(false);

    const [isFixed, setIsFixed] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerWidth <= 767) {
                setIsFixed(false);
                return;
            }
            const scrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            const distanceFromBottom =
                documentHeight - (scrollTop + windowHeight);

            if (scrollTop > 100 && distanceFromBottom > 400) {
                setIsFixed(true);
            } else {
                setIsFixed(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        dispatch(fetchSessionDetail(id));
        return () => {
            dispatch(handleSessionDetailData({ data: null, loading: false }));
        };
    }, [id]);

    useEffect(() => {
        if (!isConnected) return;

        const handleJoin = () => {
            SocketService.send("session_details", {
                session_id: id,
                user_id: user?.user_id ?? "",
            });
        };

        const handleDetail = (response) => {
            if (response?.error) {
                console.error(response.error);
                return;
            }
            const sessionData = response?.data;
            if (!sessionData) return;
            setSyncDetailData(sessionData);
        };

        const handleReconnect = () => {
            SocketService.send("joinAuctionSessionDetail", { session_id: id });
        };

        SocketService.on("joinedAuctionSessionDetail", handleJoin);
        SocketService.on("session_details", handleDetail);
        SocketService.on("connect", handleReconnect);

        SocketService.send("joinAuctionSessionDetail", { session_id: id });

        return () => {
            SocketService.off("joinedAuctionSessionDetail", handleJoin);
            SocketService.off("session_details", handleDetail);
            SocketService.off("connect", handleReconnect);
        };
    }, [isConnected, id, user?.user_id]);

    const displayData = syncDetailData
        ? (() => {
            const socketUserMatches =
                syncDetailData.user_id != null &&
                Number(syncDetailData.user_id) === Number(user?.user_id);
            return {
                ...data,
                status: syncDetailData.session_status_id ?? data?.status,
                name: syncDetailData.session_name ?? data?.name,
                no_lot: syncDetailData.no_lots ?? data?.no_lot,
                minimum_deposit:
                    syncDetailData.deposit_amount ?? data?.minimum_deposit,
                start_date:
                    syncDetailData.session_start_date ?? data?.start_date,
                auctioneer_name:
                    syncDetailData.auctioneer_name ?? data?.auctioneer_name,
                is_registered:
                    socketUserMatches && syncDetailData.is_registered != null
                        ? Number(syncDetailData.is_registered) === 1
                        : data?.is_registered,
                is_payment_completed:
                    socketUserMatches &&
                        syncDetailData.is_payment_completed != null
                        ? Number(syncDetailData.is_payment_completed) === 1
                        : data?.is_payment_completed,
                is_registration_approved:
                    socketUserMatches &&
                        syncDetailData.is_registration_approved != null
                        ? Number(syncDetailData.is_registration_approved) ===
                        1
                        : data?.is_registration_approved,
                total_deposit_amount: socketUserMatches
                    ? (syncDetailData.total_deposit_amount ??
                        data?.total_deposit_amount)
                    : data?.total_deposit_amount,
                no_registration:
                    syncDetailData.no_registration ?? data?.no_registration,
                lots: (() => {
                    if (!syncDetailData.all_lots?.length)
                        return data?.lots ?? [];
                    const socketLotMap = new Map(
                        syncDetailData.all_lots.map((l) => [Number(l.id), l])
                    );
                    return (data?.lots ?? []).map((existingLot) => {
                        const socketLot = socketLotMap.get(
                            Number(existingLot.id)
                        );
                        if (!socketLot) return existingLot;
                        return {
                            ...existingLot,
                            status_id:
                                socketLot.status_id ?? existingLot.status_id,
                            pause_resume:
                                socketLot.pause_resume ??
                                existingLot.pause_resume,
                            winner_id:
                                socketLot.winner_id ?? existingLot.winner_id,
                            is_initiated:
                                socketLot.is_initiated ??
                                existingLot.is_initiated,
                            sold_price:
                                socketLot.sold_price ??
                                existingLot.sold_price,
                            estimated_price:
                                socketLot.estimated_price ??
                                existingLot.estimated_price,
                        };
                    });
                })(),
                bid_limit_multiplier:
                    syncDetailData.bid_limit_multiplier ??
                    data?.bid_limit_multiplier,
            };
        })()
        : data;

    useEffect(() => {
        if (displayData?.is_register_interest != null) {
            setIsRegisteredInterest(Boolean(displayData.is_register_interest));
        }
    }, [displayData?.is_register_interest]);

    const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
    const isLive = displayData?.status === 1;
    const isEnded = [8, 9, 16, 35, 38].includes(displayData?.status);
    const isUpcoming = displayData?.status === 37;
    const isOpenReg = displayData?.status === 41;
    const isRegisteredAndPaid =
        displayData?.is_registered &&
        (displayData?.is_payment_completed ||
            displayData?.is_registration_approved);
    const isRegisteredNotPaid =
        displayData?.is_registered && !displayData?.is_payment_completed;
    const isRegistrationApproved = Boolean(
        displayData?.is_registration_approved
    );
    const wonLots =
        isEnded && isRegisteredAndPaid
            ? (displayData?.lots ?? []).filter(
                (lot) =>
                    lot.winner_id &&
                    Number(lot.winner_id) === Number(user?.user_id)
            )
            : [];
    const isWinner = wonLots.length > 0;

    const endedLotStatusIds = [8, 9, 16, 35, 38];
    const activeLotDetail =
        displayData?.lots?.find(
            (l) => Number(l.status_id) === 1 && Number(l.pause_resume) === 1
        ) ?? null;
    const endedLotsCount = (displayData?.lots ?? []).filter((l) =>
        endedLotStatusIds.includes(Number(l.status_id))
    ).length;
    const activeLotNumber = activeLotDetail ? endedLotsCount + 1 : null;

    const bidLimit =
        (displayData?.bid_limit_multiplier ?? 1) *
        Number(displayData?.minimum_deposit ?? 0);

    const { className: statusClass, label: statusLabel } = getSessionStatus(
        displayData?.status ?? 0
    );
    const { dateLabel, timeLabel } = getSessionDate(
        displayData?.start_date,
        isEnded
    );

    const sessionImage = displayData?.session_images?.[0]
        ? `${process.env.REACT_APP_AZURE_BLOB_URL}${displayData.session_images[0].bucket_name}/${displayData.session_images[0].doc_file_name}`
        : "/img/slide-1.jpg";

    const projectImage = displayData?.project_data?.project_images
        ? `${process.env.REACT_APP_AZURE_BLOB_URL}${displayData.project_data.project_images.bucket_name}/${displayData.project_data.project_images.image}`
        : "/img/slide-1.jpg";

    const getStartDateLabel = (startDate) => {
        if (!startDate) return "";
        const date = moment(startDate);
        const diffDays = date
            .clone()
            .startOf("day")
            .diff(moment().startOf("day"), "days");
        const timeStr = date.format("h:mm A");
        if (diffDays === 0) return `Today at ${timeStr}`;
        if (diffDays === 1) return `Tomorrow at ${timeStr}`;
        if (diffDays >= 2 && diffDays <= 7)
            return `${date.format("dddd")} at ${timeStr}`;
        return `${date.format("ddd, MMM D")} • ${timeStr}`;
    };

    const bidderRegState = {
        selectedSessionID: displayData?.id,
        propertyId: displayData?.lots?.[0]?.property_id,
        propertyName: displayData?.name,
        lotID: displayData?.lots?.[0]?.id,
        currentPathName: `${window.location.origin}/session-details/${displayData?.id}`,
    };

    const handleAuth = (action) => {
        if (!isAuthenticated) {
            navigate(
                `/sign-in?redirect=${encodeURIComponent(`/session-details/${displayData?.id || id}`)}`
            );
            return;
        }
        action();
    };

    return (
        <>
            <Helmet>
                <meta
                    name="keywords"
                    content={t(
                        "Bidhome-Adres, CRE, brokers, investment, commercial real estate, sales, auction"
                    )}
                />
                <meta
                    name="description"
                    content={t(
                        "Bidhome-Adres brings buyers, sellers, and brokers together to efficiently market and close commercial real estate deals in online CRE auctions."
                    )}
                />
                <title>
                    {displayData?.name
                        ? `${displayData.name} - Session Detail`
                        : t("Property Auction - Session Detail")}
                </title>
            </Helmet>
            <Layout>
                {loading ? (
                    <section className="details-wrap pb-5">
                        <div className="container">
                            <div className="row">
                                <div className="col-lg-8">
                                    <Shimmer
                                        type="rectangle"
                                        width="100%"
                                        height="400px"
                                        borderRadius="12px"
                                    />
                                    <div style={{ marginTop: 20 }}>
                                        <Shimmer
                                            type="rectangle"
                                            width="100%"
                                            height="200px"
                                            borderRadius="12px"
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-4">
                                    <Shimmer
                                        type="rectangle"
                                        width="100%"
                                        height="350px"
                                        borderRadius="12px"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                ) : (
                    <section className="details-wrap pb-5">
                        <div className="container">
                            <div className="row">
                                {/* ── LEFT PANEL ── */}
                                <div className="col-lg-8">
                                    {/* Gallery */}
                                    <div className="session-gallery">
                                        <figure>
                                            <img
                                                className="slide-fixed"
                                                src="/img/trans16x9.png"
                                                alt=""
                                            />
                                            <img
                                                className="slide-img"
                                                src={sessionImage}
                                                alt=""
                                            />
                                            <figcaption>
                                                <h1>{displayData?.name}</h1>
                                                <div className="date">
                                                    <i className="fa-regular fa-calendar"></i>{" "}
                                                    {dateLabel}
                                                    {timeLabel &&
                                                        ` • ${timeLabel}`}
                                                </div>
                                            </figcaption>
                                        </figure>
                                    </div>

                                    {/* Session Info */}
                                    <div className="session-gallery-data">
                                        <div className="top">
                                            <div className="session-left">
                                                <ul>
                                                    <StartsInBadge startDate={displayData?.start_date} />
                                                    <li>
                                                        <span
                                                            className={`status ${statusClass}`}
                                                        >
                                                            {statusLabel}
                                                        </span>
                                                    </li>
                                                </ul>
                                                <h3>{displayData?.name}</h3>
                                            </div>

                                            <ul className="session-count">
                                                <li>
                                                    <h5>
                                                        {displayData?.no_lot}
                                                        <span>LOTS</span>
                                                    </h5>
                                                </li>
                                                <li>
                                                    <h5>
                                                        {
                                                            displayData?.no_registration
                                                        }
                                                        <span>REGISTERED</span>
                                                    </h5>
                                                </li>
                                            </ul>
                                        </div>

                                        <ul className="location-list">
                                            <li>
                                                <img
                                                    src="/img/cal-icon.svg"
                                                    alt=""
                                                />
                                                {dateLabel}
                                                {timeLabel && (
                                                    <span> • {timeLabel}</span>
                                                )}
                                            </li>
                                            <li>
                                                <img src="/img/live-icon.svg" alt="" /> Live Online
                                            </li>
                                            <li>
                                                <img src="/img/venue-icon.svg" alt="" /> <span>Abu Dhabi</span>
                                            </li>
                                        </ul>

                                        <div className="session-author">
                                            <figure>
                                                <img
                                                    className="slide-fixed"
                                                    src="/img/cal-trans-today.png"
                                                    alt=""
                                                />
                                                <img
                                                    className="slide-img"
                                                    src="/img/logo.svg"
                                                    alt=""
                                                />
                                            </figure>
                                            <h6>
                                                {displayData?.auctioneer_name}{" "}
                                                <em>• {t("Auctioneer")}</em>
                                            </h6>
                                        </div>
                                    </div>

                                    {/* Project Details */}
                                    {displayData?.project_data && (
                                        <div className="session-project-detail-wrap">
                                            <h5>{t("Project Details")}</h5>
                                            <div className="session-project-detail">
                                                <figure>
                                                    <img
                                                        className="slide-fixed"
                                                        src="/img/trans16x9.png"
                                                        alt=""
                                                    />
                                                    <img
                                                        className="slide-img"
                                                        src={projectImage}
                                                        alt=""
                                                    />
                                                </figure>
                                                <figcaption>
                                                    <div className="name-location">
                                                        <div>
                                                            <h5 className="name">
                                                                {lang === "en"
                                                                    ? displayData
                                                                        .project_data
                                                                        .project_name
                                                                    : displayData
                                                                        .project_data
                                                                        .project_name_ar ||
                                                                    displayData
                                                                        .project_data
                                                                        .project_name}
                                                            </h5>
                                                            <div className="location">
                                                                <img
                                                                    src="/img/venue-icon.svg"
                                                                    alt=""
                                                                />
                                                                {
                                                                    displayData
                                                                        .project_data
                                                                        .community
                                                                }
                                                                ,{" "}
                                                                {
                                                                    displayData
                                                                        .project_data
                                                                        .district
                                                                }
                                                            </div>
                                                        </div>
                                                        <span className="status upcoming">
                                                            Ready
                                                        </span>
                                                    </div>
                                                    <div className="bottom">
                                                        <ul className="l-left">
                                                            <li>
                                                                <img
                                                                    src="/img/apartment-icon.svg"
                                                                    alt=""
                                                                />{" "}
                                                                {displayData
                                                                    ?.project_data
                                                                    ?.property_type
                                                                    ?.length
                                                                    ? displayData.project_data.property_type
                                                                        .map(
                                                                            (
                                                                                item
                                                                            ) =>
                                                                                t(
                                                                                    item
                                                                                )
                                                                        )
                                                                        .join(
                                                                            " | "
                                                                        )
                                                                    : "NA"}
                                                            </li>
                                                            <li>
                                                                <img
                                                                    src="/img/unit-icon.svg"
                                                                    alt=""
                                                                />
                                                                {
                                                                    displayData
                                                                        ?.project_data
                                                                        ?.total_units
                                                                }{" "}
                                                                {t("Units")}
                                                            </li>
                                                        </ul>
                                                        <ul className="r-right">
                                                            <li className="pricing">
                                                                <span>
                                                                    {t(
                                                                        "Starting Price:"
                                                                    )}
                                                                </span>{" "}
                                                                <em className="dirham">
                                                                    D
                                                                </em>{" "}
                                                                {formatPrice(
                                                                    displayData
                                                                        .project_data
                                                                        .starting_price,
                                                                    lang
                                                                )}
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </figcaption>
                                            </div>
                                        </div>
                                    )}

                                    {/* Auction Catalog */}
                                    {displayData?.lots?.length > 0 && (
                                        <div className={`auction-catalog ${!displayData?.project_data ? "no-project" : ""}`}>
                                            <h5 className='heading'>
                                                {t("Auction Catalog")}
                                                <span>
                                                    {t(
                                                        "Explore our curated catalog of premier properties."
                                                    )}
                                                </span>
                                            </h5>
                                            <ul className="auction-catalog-list">
                                                {displayData.lots.map((lot) => {
                                                    const lotImg =
                                                        lot.property_image
                                                            ?.image &&
                                                            lot.property_image
                                                                ?.bucket_name
                                                            ? `${process.env.REACT_APP_AZURE_BLOB_URL}${lot.property_image.bucket_name}/${lot.property_image.image}`
                                                            : "/img/slide-1.jpg";
                                                    return (
                                                        <li key={lot.id}>
                                                            <figure>
                                                                <img
                                                                    className="slide-fixed"
                                                                    src="/img/trans16x9.png"
                                                                    alt=""
                                                                />
                                                                <img
                                                                    className="slide-img"
                                                                    src={lotImg}
                                                                    alt=""
                                                                />
                                                            </figure>
                                                            <figcaption>
                                                                <div className="top">
                                                                    <div>
                                                                        <ul className="ids">
                                                                            <li>
                                                                                {
                                                                                    lot.property_type
                                                                                }
                                                                            </li>
                                                                            <li>
                                                                                {
                                                                                    lot.auction_id
                                                                                }
                                                                            </li>
                                                                        </ul>
                                                                        <h5>
                                                                            <em className="dirham">
                                                                                D
                                                                            </em>{" "}
                                                                            {formatPrice(
                                                                                lot.estimated_price,
                                                                                lang
                                                                            )}
                                                                            <span>
                                                                                {t(
                                                                                    "Est. Price"
                                                                                )}
                                                                            </span>
                                                                        </h5>
                                                                    </div>
                                                                    <span className={`status ${getLotStatus(displayData?.status, lot?.status_id, lot?.is_initiated, true, false)?.className}`}>
                                                                        {t(getLotStatus(displayData?.status, lot?.status_id, lot?.is_initiated, true, false)?.label)}
                                                                    </span>
                                                                </div>
                                                                <ul className="types">
                                                                    <li>
                                                                        <img
                                                                            src="/img/bed-icon.svg"
                                                                            alt=""
                                                                        />
                                                                        {`${lot.beds}`.toLowerCase() !==
                                                                            "studio"
                                                                            ? toEasternArabicNumerals(
                                                                                lot.beds
                                                                                    ?.slice(
                                                                                        0,
                                                                                        2
                                                                                    )
                                                                                    .trim(),
                                                                                lang
                                                                            )
                                                                            : ""}
                                                                        <span>
                                                                            {`${lot.beds}`.toLowerCase() ===
                                                                                "studio"
                                                                                ? t(
                                                                                    "Studio"
                                                                                )
                                                                                : t(
                                                                                    toEasternArabicNumerals(
                                                                                        lot.beds
                                                                                            ?.slice(
                                                                                                0,
                                                                                                2
                                                                                            )
                                                                                            .trim(),
                                                                                        lang
                                                                                    ) ===
                                                                                        "1"
                                                                                        ? "Bed"
                                                                                        : "Beds"
                                                                                )}
                                                                        </span>
                                                                    </li>
                                                                    <li>
                                                                        <img
                                                                            src="/img/bath-icon.svg"
                                                                            alt=""
                                                                        />{" "}
                                                                        {
                                                                            lot.baths
                                                                        }{" "}
                                                                        <span>
                                                                            {t(
                                                                                "Baths"
                                                                            )}
                                                                        </span>
                                                                    </li>
                                                                    <li>
                                                                        <img
                                                                            src="/img/ruler-icon.svg"
                                                                            alt=""
                                                                        />{" "}
                                                                        {
                                                                            lot.square_footage
                                                                        }{" "}
                                                                        <span>
                                                                            {t(
                                                                                "sqft"
                                                                            )}
                                                                        </span>
                                                                    </li>
                                                                </ul>
                                                                <div className="name-location">
                                                                    <h6 className="name">
                                                                        {lang ===
                                                                            "en"
                                                                            ? lot.property_name
                                                                            : lot.property_name_ar ||
                                                                            lot.property_name}
                                                                    </h6>
                                                                    <div className="location">
                                                                        <img
                                                                            src="/img/venue-icon.svg"
                                                                            alt=""
                                                                        />
                                                                        {
                                                                            lot.community
                                                                        }
                                                                        ,{" "}
                                                                        {
                                                                            lot.state
                                                                        }
                                                                    </div>
                                                                </div>
                                                                <div className="bottom">
                                                                    <button
                                                                        className="btn btn-white btn-xs more-btn"
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/property/detail/${lot.property_id}/detail`
                                                                            )
                                                                        }
                                                                    >
                                                                        {t(
                                                                            "More Details"
                                                                        )}{" "}
                                                                        <i className="fa-solid fa-angle-right ml4"></i>
                                                                    </button>
                                                                </div>
                                                            </figcaption>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* ── RIGHT SIDEBAR ── */}
                                <div className="col-lg-4">
                                    <div className={`sidebar-session ${isFixed ? "sticky" : ""}`}>
                                        <div className="session-bg">
                                            {/* OpenReg — not registered */}
                                            {isOpenReg &&
                                                !displayData?.is_registered && (
                                                    <div className="step-1">
                                                        <div className="head">
                                                            <h5>
                                                                {t(
                                                                    "Auction Registration"
                                                                )}
                                                            </h5>
                                                            <span className="status success">
                                                                {t("Open")}
                                                            </span>
                                                        </div>
                                                        <div className="refund-box">
                                                            <div className="refund-deposit">
                                                                <h4>
                                                                    <span>
                                                                        {t(
                                                                            "Refundable deposit from"
                                                                        )}
                                                                    </span>
                                                                    <em className="dirham">
                                                                        D
                                                                    </em>{" "}
                                                                    {formatPrice(
                                                                        displayData?.minimum_deposit,
                                                                        lang
                                                                    )}
                                                                </h4>
                                                                <p className="tagline">
                                                                    {t(
                                                                        "This lets you place bids up to"
                                                                    )}{" "}
                                                                    <strong>
                                                                        <em className="dirham">
                                                                            D
                                                                        </em>{" "}
                                                                        {formatPrice(
                                                                            bidLimit,
                                                                            lang
                                                                        )}
                                                                        .
                                                                    </strong>{" "}
                                                                    {t(
                                                                        "Adjustable anytime."
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <div className="refund-tagline">
                                                                <img
                                                                    src="/img/refund-icon.svg"
                                                                    alt="Refund Icon"
                                                                />{" "}
                                                                {t(
                                                                    "100% refund if you don't win"
                                                                )}
                                                            </div>
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() =>
                                                                    handleAuth(
                                                                        () =>
                                                                            navigate(
                                                                                "/bidder-registration",
                                                                                {
                                                                                    state: bidderRegState,
                                                                                }
                                                                            )
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    "Register as a bidder"
                                                                )}
                                                            </button>
                                                            <p className="secure-text mb0">
                                                                {t(
                                                                    "Secure Payment"
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                            {/* OpenReg — registered but payment not completed */}
                                            {isOpenReg &&
                                                isRegisteredNotPaid && (
                                                    <div className="step-6">
                                                        <div className="head">
                                                            <h5>
                                                                {t(
                                                                    "Complete Registration"
                                                                )}
                                                            </h5>
                                                            <span className="status soon">
                                                                {/* {t("Pending")} */}
                                                                {t("Declined")}
                                                            </span>
                                                        </div>
                                                        <div className="refund-box">
                                                            <div className="refund-deposit">
                                                                <h4>
                                                                    <span>
                                                                        {t(
                                                                            "Deposit Required"
                                                                        )}
                                                                    </span>
                                                                    <em className="dirham">
                                                                        D
                                                                    </em>{" "}
                                                                    {formatPrice(
                                                                        displayData?.minimum_deposit,
                                                                        lang
                                                                    )}
                                                                </h4>
                                                                <p className="tagline">
                                                                    {t(
                                                                        "Complete your deposit to confirm your bidder registration."
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() =>
                                                                    handleAuth(
                                                                        () =>
                                                                            navigate(
                                                                                "/bidder-registration",
                                                                                {
                                                                                    state: bidderRegState,
                                                                                }
                                                                            )
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    "Complete Registration"
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            {/* OpenReg — registered + paid, approval pending */}
                                            {isOpenReg &&
                                                isRegisteredAndPaid &&
                                                !isRegistrationApproved && (
                                                    <div className="step-7">
                                                        <h5>
                                                            <i className="fa-solid fa-circle-check lightgray-text"></i>{" "}
                                                            {t(
                                                                "You're Registered"
                                                            )}
                                                            <span className="badge-approval-pending">
                                                                {t(
                                                                    "Approval Pending"
                                                                )}
                                                            </span>
                                                        </h5>
                                                        <div className="refund-box">
                                                            <div className="refund-deposit">
                                                                <h4>
                                                                    <span>
                                                                        {t(
                                                                            "Deposit Paid"
                                                                        )}
                                                                    </span>
                                                                    <em className="dirham">
                                                                        D
                                                                    </em>{" "}
                                                                    {formatPrice(
                                                                        displayData?.total_deposit_amount ?? displayData?.minimum_deposit,
                                                                        lang
                                                                    )}{" "}
                                                                    <i className="fa-solid fa-circle-check lightgray-text"></i>
                                                                </h4>
                                                                <p className="tagline">
                                                                    {t(
                                                                        "Your registration is under review."
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <div className="refund-timeline">
                                                                <img
                                                                    src="/img/clock-icon.svg"
                                                                    alt=""
                                                                />
                                                                <span>
                                                                    {t(
                                                                        "Live Auction Starts in"
                                                                    )}{" "}
                                                                    <CountdownText
                                                                        startDate={
                                                                            displayData?.start_date
                                                                        }
                                                                    />
                                                                    <br />
                                                                    {getStartDateLabel(
                                                                        displayData?.start_date
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <button
                                                                className="btn btn-white btn-sm"
                                                                onClick={() =>
                                                                    document
                                                                        .querySelector(
                                                                            ".auction-catalog"
                                                                        )
                                                                        ?.scrollIntoView(
                                                                            {
                                                                                behavior:
                                                                                    "smooth",
                                                                            }
                                                                        )
                                                                }
                                                            >
                                                                {t(
                                                                    "View Auction Catalog"
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            {/* Upcoming — registered interest */}
                                            {isUpcoming &&
                                                isRegisteredInterest &&
                                                !isRegisteredAndPaid && (
                                                    <div className="step-5">
                                                        <div className="head">
                                                            <h5>
                                                                {t(
                                                                    "Auction Registration"
                                                                )}
                                                            </h5>
                                                            <span className="status soon">
                                                                {t("Soon")}
                                                            </span>
                                                        </div>
                                                        <div className="refund-box">
                                                            <div className="registration-opens">
                                                                <div className="icon">
                                                                    <img
                                                                        src="/img/bell-icon.svg"
                                                                        alt="bell icon"
                                                                    />
                                                                </div>
                                                                <h6>
                                                                    {t(
                                                                        "You're on the List"
                                                                    )}
                                                                </h6>
                                                                <p className="tagline">
                                                                    {t(
                                                                        "We'll let you know as soon as registration for this auction becomes available."
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <button
                                                                className="btn btn-white btn-sm"
                                                                onClick={() =>
                                                                    document
                                                                        .querySelector(
                                                                            ".auction-catalog"
                                                                        )
                                                                        ?.scrollIntoView(
                                                                            {
                                                                                behavior:
                                                                                    "smooth",
                                                                            }
                                                                        )
                                                                }
                                                            >
                                                                {t(
                                                                    "View Auction Catalog"
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            {/* Upcoming — not registered interest */}
                                            {isUpcoming &&
                                                !isRegisteredInterest &&
                                                !isRegisteredAndPaid && (
                                                    <div className="step-5">
                                                        <div className="head">
                                                            <h5>
                                                                {t(
                                                                    "Auction Registration"
                                                                )}
                                                            </h5>
                                                            <span className="status soon">
                                                                {t("Soon")}
                                                            </span>
                                                        </div>
                                                        <div className="refund-box">
                                                            <div className="registration-opens">
                                                                <div className="icon">
                                                                    <img
                                                                        src="/img/clock-icon.svg"
                                                                        alt="clock icon"
                                                                    />
                                                                </div>
                                                                <h6>
                                                                    {t(
                                                                        "Registration Opens Soon"
                                                                    )}
                                                                </h6>
                                                                <p className="tagline">
                                                                    {t(
                                                                        "This auction is not open for registration yet. Register your interest to receive updates."
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() =>
                                                                    handleAuth(
                                                                        () =>
                                                                            setShowRegisterInterestModal(
                                                                                true
                                                                            )
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    "Register Your interest"
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            {/* Upcoming / OpenReg approved — registered + paid */}
                                            {(isUpcoming ||
                                                (isOpenReg &&
                                                    isRegistrationApproved)) &&
                                                isRegisteredAndPaid && (
                                                    <div className="step-7">
                                                        <h5>
                                                            <i className="fa-solid fa-circle-check green-icon"></i>{" "}
                                                            {t(
                                                                "You're Registered"
                                                            )}
                                                        </h5>
                                                        <div className="refund-box">
                                                            <div className="refund-deposit">
                                                                <h4>
                                                                    <span>
                                                                        {t(
                                                                            "Deposit Paid"
                                                                        )}
                                                                    </span>
                                                                    <em className="dirham">
                                                                        D
                                                                    </em>{" "}
                                                                    {formatPrice(
                                                                        displayData?.total_deposit_amount ?? displayData?.minimum_deposit,
                                                                        lang
                                                                    )}{" "}
                                                                    <i className="fa-solid fa-circle-check lightgray-text"></i>
                                                                </h4>
                                                                <p className="tagline">
                                                                    {t(
                                                                        "You can bid up to"
                                                                    )}{" "}
                                                                    <em className="dirham">
                                                                        D
                                                                    </em>{" "}
                                                                    {formatPrice(
                                                                        bidLimit,
                                                                        lang
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <div className="refund-timeline">
                                                                <img
                                                                    src="/img/clock-icon.svg"
                                                                    alt="Refund Icon"
                                                                />
                                                                <span>
                                                                    {t(
                                                                        "Live Auction Starts in"
                                                                    )}{" "}
                                                                    <CountdownText
                                                                        startDate={
                                                                            displayData?.start_date
                                                                        }
                                                                    />
                                                                    <br />
                                                                    {getStartDateLabel(
                                                                        displayData?.start_date
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <button
                                                                className="btn btn-white btn-sm"
                                                                onClick={() =>
                                                                    document
                                                                        .querySelector(
                                                                            ".auction-catalog"
                                                                        )
                                                                        ?.scrollIntoView(
                                                                            {
                                                                                behavior:
                                                                                    "smooth",
                                                                            }
                                                                        )
                                                                }
                                                            >
                                                                {t(
                                                                    "View Auction Catalog"
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            {/* Live — not registered/paid */}
                                            {isLive && !isRegisteredAndPaid && (
                                                <div className="step-3">
                                                    <div className="refund-box">
                                                        <div className="auctionlive">
                                                            <h6>
                                                                {t(
                                                                    "Auction is Live now"
                                                                )}
                                                                {activeLotDetail && (
                                                                    <span>
                                                                        {` Lot ${activeLotNumber} of ${displayData?.no_lot} • `}
                                                                        {lang ===
                                                                            "en"
                                                                            ? activeLotDetail.property_name
                                                                            : activeLotDetail.property_name_ar ||
                                                                            activeLotDetail.property_name}
                                                                    </span>
                                                                )}
                                                            </h6>
                                                        </div>
                                                        <div className="refund-deposit">
                                                            <h4>
                                                                <span>
                                                                    {t(
                                                                        "Refundable deposit from"
                                                                    )}
                                                                </span>
                                                                <em className="dirham">
                                                                    D
                                                                </em>{" "}
                                                                {formatPrice(
                                                                    displayData?.minimum_deposit,
                                                                    lang
                                                                )}
                                                            </h4>
                                                            <p className="tagline">
                                                                {t(
                                                                    "This lets you place bids up to"
                                                                )}{" "}
                                                                <strong>
                                                                    <em className="dirham">
                                                                        D
                                                                    </em>{" "}
                                                                    {formatPrice(
                                                                        bidLimit,
                                                                        lang
                                                                    )}
                                                                    .
                                                                </strong>{" "}
                                                                {t(
                                                                    "Adjustable anytime."
                                                                )}
                                                            </p>
                                                        </div>
                                                        <div className="refund-tagline">
                                                            <img
                                                                src="/img/refund-icon.svg"
                                                                alt="Refund Icon"
                                                            />{" "}
                                                            {t(
                                                                "100% refund if you don't win"
                                                            )}
                                                        </div>
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            onClick={() =>
                                                                handleAuth(() =>
                                                                    navigate(
                                                                        "/bidder-registration",
                                                                        {
                                                                            state: bidderRegState,
                                                                        }
                                                                    )
                                                                )
                                                            }
                                                        >
                                                            {t(
                                                                "Register as a bidder"
                                                            )}
                                                        </button>
                                                        <p className="secure-text mb0">
                                                            {t(
                                                                "Just watching?"
                                                            )}{" "}
                                                            <button
                                                                className="btn-link primary-text"
                                                                onClick={() =>
                                                                    navigate(
                                                                        "/live-auction-details",
                                                                        {
                                                                            state: {
                                                                                selectedSessionID:
                                                                                    displayData?.id,
                                                                            },
                                                                        }
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    "Enter as Viewer"
                                                                )}
                                                            </button>
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Live — registered + paid, approval pending */}
                                            {isLive &&
                                                isRegisteredAndPaid &&
                                                !isRegistrationApproved && (
                                                    <div className="step-4-pending">
                                                        <div className="refund-box">
                                                            <div className="auctionlive">
                                                                <h6>
                                                                    {t(
                                                                        "Auction is Live now"
                                                                    )}
                                                                    {activeLotDetail && (
                                                                        <span>
                                                                            {` Lot ${activeLotNumber} of ${displayData?.no_lot} • `}
                                                                            {lang ===
                                                                                "en"
                                                                                ? activeLotDetail.property_name
                                                                                : activeLotDetail.property_name_ar ||
                                                                                activeLotDetail.property_name}
                                                                        </span>
                                                                    )}
                                                                </h6>
                                                            </div>
                                                            <div className="refund-deposit">
                                                                <h4>
                                                                    <span>
                                                                        {t(
                                                                            "Deposit Paid"
                                                                        )}
                                                                    </span>
                                                                    <em className="dirham">
                                                                        D
                                                                    </em>{" "}
                                                                    {formatPrice(
                                                                        displayData?.total_deposit_amount ?? displayData?.minimum_deposit,
                                                                        lang
                                                                    )}{" "}
                                                                    <i className="fa-solid fa-circle-check lightgray-text"></i>
                                                                </h4>
                                                                <p className="tagline">
                                                                    {t(
                                                                        "Your registration is under review."
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                disabled
                                                            >
                                                                {t(
                                                                    "Approval Pending"
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            {/* Live — registered + paid, approved */}
                                            {isLive &&
                                                isRegisteredAndPaid &&
                                                isRegistrationApproved && (
                                                    <div className="step-4">
                                                        <div className="refund-box">
                                                            <div className="auctionlive">
                                                                <h6>
                                                                    {t(
                                                                        "Auction is Live now"
                                                                    )}
                                                                    {activeLotDetail && (
                                                                        <span>
                                                                            {` Lot ${activeLotNumber} of ${displayData?.no_lot} • `}
                                                                            {lang ===
                                                                                "en"
                                                                                ? activeLotDetail.property_name
                                                                                : activeLotDetail.property_name_ar ||
                                                                                activeLotDetail.property_name}
                                                                        </span>
                                                                    )}
                                                                </h6>
                                                            </div>
                                                            <div className="refund-deposit">
                                                                <h4>
                                                                    <span>
                                                                        {t(
                                                                            "Deposit Paid"
                                                                        )}
                                                                    </span>
                                                                    <em className="dirham">
                                                                        D
                                                                    </em>{" "}
                                                                    {formatPrice(
                                                                        displayData?.total_deposit_amount ?? displayData?.minimum_deposit,
                                                                        lang
                                                                    )}{" "}
                                                                    <i className="fa-solid fa-circle-check lightgray-text"></i>
                                                                </h4>
                                                                <p className="tagline">
                                                                    {t(
                                                                        "You can bid up to"
                                                                    )}{" "}
                                                                    <em className="dirham">
                                                                        D
                                                                    </em>{" "}
                                                                    {formatPrice(
                                                                        bidLimit,
                                                                        lang
                                                                    )}
                                                                </p>
                                                                <button
                                                                    className="btn btn-white btn-sm"
                                                                    onClick={() =>
                                                                        handleAuth(
                                                                            () =>
                                                                                navigate(
                                                                                    "/bidder-registration",
                                                                                    {
                                                                                        state: {
                                                                                            ...bidderRegState,
                                                                                            isBidLimitIncrease: true,
                                                                                        },
                                                                                    }
                                                                                )
                                                                        )
                                                                    }
                                                                >
                                                                    {t(
                                                                        "Increase your max bid"
                                                                    )}
                                                                </button>
                                                            </div>
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() =>
                                                                    handleAuth(
                                                                        () =>
                                                                            navigate(
                                                                                "/live-auction-details",
                                                                                {
                                                                                    state: {
                                                                                        selectedSessionID:
                                                                                            displayData?.id,
                                                                                    },
                                                                                }
                                                                            )
                                                                    )
                                                                }
                                                            >
                                                                {t("Join Now")}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            {/* Ended — winner (registered + paid + won lots) */}
                                            {isEnded && isWinner && (
                                                <div className="step-winner">
                                                    <div className="head">
                                                        <h5>
                                                            <span className="trophy-icon">
                                                                <i className="fa-solid fa-trophy"></i>
                                                            </span>
                                                            {wonLots.length ===
                                                                1
                                                                ? t("You Won!")
                                                                : t(
                                                                    "You Won {{count}} lots!",
                                                                    {
                                                                        count: wonLots.length,
                                                                    }
                                                                )}
                                                        </h5>
                                                        <span className="status closed">
                                                            {t("Closed")}
                                                        </span>
                                                    </div>

                                                    {wonLots.length === 1 ? (
                                                        wonLots.map((lot) => {
                                                            const lotImg =
                                                                lot
                                                                    .property_image
                                                                    ?.image &&
                                                                    lot
                                                                        .property_image
                                                                        ?.bucket_name
                                                                    ? `${process.env.REACT_APP_AZURE_BLOB_URL}${lot.property_image.bucket_name}/${lot.property_image.image}`
                                                                    : "/img/slide-1.jpg";
                                                            return (
                                                                <div
                                                                    key={lot.id}
                                                                    className="won-single-lot"
                                                                >
                                                                    <div className="won-lot-card">
                                                                        <figure>
                                                                            <img
                                                                                src={
                                                                                    lotImg
                                                                                }
                                                                                alt=""
                                                                            />
                                                                        </figure>
                                                                        <div className="lot-meta">
                                                                            <span className="lot-auction-id">
                                                                                {
                                                                                    lot.auction_id
                                                                                }
                                                                            </span>
                                                                            <h6>
                                                                                {lang ===
                                                                                    "en"
                                                                                    ? lot.property_name
                                                                                    : lot.property_name_ar ||
                                                                                    lot.property_name}
                                                                            </h6>
                                                                            {lot.sold_price &&
                                                                                Number(
                                                                                    lot.sold_price
                                                                                ) >
                                                                                0 && (
                                                                                    <>
                                                                                        <hr className="lot-divider" />
                                                                                        <span className="hammer-label">
                                                                                            {t(
                                                                                                "Hummer Price"
                                                                                            )}
                                                                                        </span>
                                                                                        <p className="hammer-value">
                                                                                            <em className="dirham">
                                                                                                D
                                                                                            </em>{" "}
                                                                                            {formatPrice(
                                                                                                lot.sold_price,
                                                                                                lang
                                                                                            )}
                                                                                        </p>
                                                                                    </>
                                                                                )}
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        className="btn btn-primary"
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/property/detail/${lot.property_id}/detail`
                                                                            )
                                                                        }
                                                                    >
                                                                        {t(
                                                                            "Property details"
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            );
                                                        })
                                                    ) : (
                                                        <div className="won-lots-list">
                                                            {wonLots.map(
                                                                (lot) => {
                                                                    const lotImg =
                                                                        lot
                                                                            .property_image
                                                                            ?.image &&
                                                                            lot
                                                                                .property_image
                                                                                ?.bucket_name
                                                                            ? `${process.env.REACT_APP_AZURE_BLOB_URL}${lot.property_image.bucket_name}/${lot.property_image.image}`
                                                                            : "/img/slide-1.jpg";
                                                                    return (
                                                                        <div
                                                                            key={
                                                                                lot.id
                                                                            }
                                                                            className="won-lot-row"
                                                                            onClick={() =>
                                                                                navigate(
                                                                                    `/property/detail/${lot.property_id}/detail`
                                                                                )
                                                                            }
                                                                        >
                                                                            <img
                                                                                src={
                                                                                    lotImg
                                                                                }
                                                                                alt=""
                                                                            />
                                                                            <div className="won-lot-row-info">
                                                                                <span className="lot-auction-id">
                                                                                    {
                                                                                        lot.auction_id
                                                                                    }
                                                                                </span>
                                                                                <h6>
                                                                                    {lang ===
                                                                                        "en"
                                                                                        ? lot.property_name
                                                                                        : lot.property_name_ar ||
                                                                                        lot.property_name}
                                                                                </h6>
                                                                            </div>
                                                                            {lot.sold_price &&
                                                                                Number(
                                                                                    lot.sold_price
                                                                                ) >
                                                                                0 && (
                                                                                    <div className="won-lot-row-price">
                                                                                        <span>
                                                                                            <em className="dirham">
                                                                                                D
                                                                                            </em>{" "}
                                                                                            {formatPrice(
                                                                                                lot.sold_price,
                                                                                                lang
                                                                                            )}
                                                                                        </span>
                                                                                        <i className="fa-solid fa-angle-right"></i>
                                                                                    </div>
                                                                                )}
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    )}

                                                    <p className="secure-text mb0">
                                                        {t("Need Help?")}{" "}
                                                        <a
                                                            href="mailto:info@propify.ae"
                                                            className="primary-text"
                                                        >
                                                            info@propify.ae
                                                        </a>
                                                    </p>
                                                </div>
                                            )}

                                            {/* Ended — registered + paid + did not win */}
                                            {isEnded &&
                                                isRegisteredAndPaid &&
                                                !isWinner && (
                                                    <div className="step-2">
                                                        <div className="head">
                                                            <h5>
                                                                {t(
                                                                    "Auction Ended"
                                                                )}
                                                            </h5>
                                                            <span className="status closed">
                                                                {t("Closed")}
                                                            </span>
                                                        </div>
                                                        <div className="refund-box">
                                                            <div className="refund-deposit">
                                                                <h4>
                                                                    <span>
                                                                        {t(
                                                                            "Refunded Deposit"
                                                                        )}
                                                                    </span>
                                                                    <em className="dirham">
                                                                        D
                                                                    </em>{" "}
                                                                    {formatPrice(
                                                                        displayData?.total_deposit_amount ??
                                                                        displayData?.minimum_deposit,
                                                                        lang
                                                                    )}
                                                                </h4>
                                                                <p className="tagline">
                                                                    {t(
                                                                        "Refunded to the same payment method. Will take up to 14 days"
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() =>
                                                                    navigate(
                                                                        "/live-auctions-listings",
                                                                        {
                                                                            state: {
                                                                                defaultType:
                                                                                    "upcoming",
                                                                            },
                                                                        }
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    "Browse Upcoming Auctions"
                                                                )}
                                                            </button>
                                                            <p className="secure-text mb0">
                                                                {t(
                                                                    "Need Help?"
                                                                )}{" "}
                                                                <a
                                                                    href="mailto:info@propify.ae"
                                                                    className="primary-text"
                                                                >
                                                                    info@propify.ae
                                                                </a>
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                            {/* Ended — not registered */}
                                            {isEnded &&
                                                !isRegisteredAndPaid && (
                                                    <div className="step-8">
                                                        <div className="head">
                                                            <h5>
                                                                {t(
                                                                    "Auction Registration"
                                                                )}
                                                            </h5>
                                                            <span className="status closed">
                                                                {t("Closed")}
                                                            </span>
                                                        </div>
                                                        <div className="refund-box">
                                                            <div className="registration-opens">
                                                                <div className="icon flag">
                                                                    <img
                                                                        src="/img/flag-icon.svg"
                                                                        alt="flag icon"
                                                                    />
                                                                </div>
                                                                <h6>
                                                                    {t(
                                                                        "Auction Ended"
                                                                    )}
                                                                </h6>
                                                                <p className="tagline">
                                                                    {t(
                                                                        "you can still participate in upcoming auctions."
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() =>
                                                                    navigate(
                                                                        "/live-auctions-listings",
                                                                        {
                                                                            state: {
                                                                                defaultType:
                                                                                    "upcoming",
                                                                            },
                                                                        }
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    "Browse Upcoming Auctions"
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}
            </Layout>

            {showRegisterInterestModal && (
                <RegisterInterestModal
                    show={showRegisterInterestModal}
                    onClose={() => setShowRegisterInterestModal(false)}
                    property={displayData?.lots?.[0]?.property_id}
                    setIsRegisteredInterest={setIsRegisteredInterest}
                    rememberMeFlag={displayData?.is_remember_interest ?? false}
                />
            )}
        </>
    );
}
